import type { CalendarDate } from "@internationalized/date";
import { Option, pipe } from "effect";
import {
  getWeekDays,
  isInWeek,
  parseCalendarDate,
  toTimeSlot,
} from "#/modules/les.planning/domain/calendar";
import { isWithoutTime, type LesplanningLes } from "#/modules/les.planning/domain/les";
import {
  getOpenSlots,
  type Roosterregel,
  type RoosterSlot,
} from "#/modules/les.planning/domain/rooster";

/** A moment a klas has gym: a les, or an open roosteruur. */
export interface Gymmoment {
  readonly key: string;
  readonly klasIds: readonly string[];
  readonly startAt: string;
}

export const lesMomentKey = (les: Pick<LesplanningLes, "id">) => les.id;
export const slotMomentKey = (slot: Pick<RoosterSlot, "key">) => `slot:${slot.key}`;

/**
 * A docent's gym moments in the week from `weekStart`: the current user's, or with `docentId` a
 * colleague's. Roosteruren that have ended still count: the first gym of the week stays les 1
 * after it is over.
 */
export const getWeekGymmomenten = ({
  weekStart,
  lessen,
  regels,
  timeZone,
  docentId,
}: {
  weekStart: CalendarDate;
  lessen: readonly LesplanningLes[];
  regels: readonly Roosterregel[];
  timeZone?: string;
  docentId?: string;
}): Gymmoment[] => {
  const isOf = (docent: { schooldocentId: string; isCurrentUser: boolean }) =>
    docentId === undefined ? docent.isCurrentUser : docent.schooldocentId === docentId;
  const ownLessen = lessen.filter((les) => {
    const slot = toTimeSlot(les.startAt, les.duurMinuten, timeZone);
    return isOf(les.docent) && isInWeek(slot.day, weekStart) && !isWithoutTime(slot);
  });
  const ownRegels = regels.filter(isOf);
  return [
    ...ownLessen.map((les) => ({
      key: lesMomentKey(les),
      klasIds: les.klassen.map((klas) => klas.id),
      startAt: les.startAt,
    })),
    ...getOpenSlots(ownRegels, getWeekDays(weekStart, 7), ownLessen, timeZone).map((slot) => ({
      key: slotMomentKey(slot),
      klasIds: slot.klassen.map((klas) => klas.id),
      startAt: slot.startAt,
    })),
  ];
};

/**
 * The how-manieth les of the week every moment is: the first gym 6A has that week is its les 1, the
 * next its les 2. Klassen that have gym together count each for themselves, and their moment takes
 * the lowest number of them.
 */
export const getLesnummers = (moments: readonly Gymmoment[]): ReadonlyMap<string, number> => {
  const perKlas = new Map<string, number>();
  const nummers = new Map<string, number>();
  const ordered = [...moments].sort(
    (a, b) => Date.parse(a.startAt) - Date.parse(b.startAt) || a.key.localeCompare(b.key),
  );
  for (const moment of ordered) {
    const klasIds = [...new Set(moment.klasIds)];
    if (klasIds.length === 0) continue;
    const counts = klasIds.map((klasId) => (perKlas.get(klasId) ?? 0) + 1);
    klasIds.forEach((klasId, index) => perKlas.set(klasId, counts[index]!));
    nummers.set(moment.key, Math.min(...counts));
  }
  return nummers;
};

/** The number a moment gets among the others of its week, such as a les still being prepared. */
export const getLesnummer = (moment: Gymmoment, others: readonly Gymmoment[]): number | null =>
  getLesnummers([...others.filter((other) => other.key !== moment.key), moment]).get(moment.key) ??
  null;

const hasEnded = (regel: Roosterregel, today: CalendarDate) =>
  pipe(
    parseCalendarDate(regel.geldigTot),
    Option.exists((until) => until.compare(today) < 0),
  );

/**
 * How often a week the busiest klas of the school has gym by the rooster, whoever gives it; 0
 * without a rooster.
 */
export const getLessenPerWeek = (regels: readonly Roosterregel[], today: CalendarDate): number => {
  const perKlas = new Map<string, number>();
  for (const regel of regels) {
    if (hasEnded(regel, today)) continue;
    for (const klas of regel.klassen) perKlas.set(klas.id, (perKlas.get(klas.id) ?? 0) + 1);
  }
  return Math.max(0, ...perKlas.values());
};
